'use client';
import React from 'react';

interface ButtonProps {
  children: React.ReactNode;
  onClick?: () => void;
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'contact-card';
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'bio';
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
  className?: string;
  fullWidth?: boolean;
  loading?: boolean;
  'aria-label'?: string;
  'aria-describedby'?: string;
  'aria-pressed'?: boolean;
  'aria-expanded'?: boolean;
}

const Button: React.FC<ButtonProps & React.ButtonHTMLAttributes<HTMLButtonElement>> = ({
  children,
  onClick,
  variant = 'primary',
  size = 'md',
  disabled = false,
  type = 'button',
  className = '',
  fullWidth = false,
  loading = false,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  'aria-pressed': ariaPressed,
  'aria-expanded': ariaExpanded,
  ...props
}) => {
  const variants = {
    primary: 'bg-primary text-primary-fg hover:bg-action-2 active:bg-primary-darker',
    secondary: 'bg-secondary text-secondary-fg hover:bg-action-2 active:bg-accent',
    outline:
      'border border-text-muted text-text-primary bg-transparent hover:bg-action-2 hover:text-white active:bg-accent',
    ghost: 'text-text-primary bg-light hover:bg-accent-warning',
    danger: 'bg-red-600 text-white hover:bg-action-2 active:bg-red-800',
    'contact-card':
      'rounded-full bg-light text-primary-darker hover:bg-accent hover:text-action-2 cursor-[url("/images/cursor.svg"),_auto]',
  };

  // Mobile-first responsive sizes
  const sizes = {
    xs: 'px-2 py-1 text-xs sm:px-2.5 sm:py-1.5 sm:text-xs',
    sm: 'px-2.5 py-1.5 text-xs sm:px-3 sm:py-2 sm:text-sm md:text-sm',
    md: 'px-3 py-2 text-sm sm:px-4 sm:py-2.5 sm:text-base md:text-base',
    lg: 'px-4 py-2.5 text-base leading-[30px] sm:px-8 sm:py-4',
    xl: 'px-5 py-3 text-lg sm:px-6 sm:py-4 sm:text-xl md:text-xl lg:text-2xl',
    bio: 'px-4 py-[7px] text-[16px] leading-[28px] font-normal',
  };

  // Responsive border radius
  const responsiveRadius = 'rounded-lg sm:rounded-lg md:rounded-lg';
  const isRoundedFull = variant === 'contact-card';
  // Responsive focus ring
  // const responsiveFocus = 'focus:ring-2 sm:focus:ring-2 md:focus:ring-4';

  // Loading spinner component
  const LoadingSpinner = () => (
    <svg data-component-id="src/components/ui/Button.tsx:65:4" data-component-path="src/components/ui/Button.tsx" data-component-line="65" data-component-end-line="77" data-component-file="Button.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22animate-spin%20h-4%20w-4%20sm%3Ah-5%20sm%3Aw-5%20mr-2%20sm%3Amr-3%22%7D"
      className="animate-spin h-4 w-4 sm:h-5 sm:w-5 mr-2 sm:mr-3"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle data-component-id="src/components/ui/Button.tsx:71:6" data-component-path="src/components/ui/Button.tsx" data-component-line="71" data-component-end-line="71" data-component-file="Button.tsx" data-component-name="circle" data-component-content="%7B%22elementName%22%3A%22circle%22%2C%22className%22%3A%22opacity-25%22%7D" className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path data-component-id="src/components/ui/Button.tsx:72:6" data-component-path="src/components/ui/Button.tsx" data-component-line="72" data-component-end-line="76" data-component-file="Button.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%2C%22className%22%3A%22opacity-75%22%7D"
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );

  return (
    <button data-component-id="src/components/ui/Button.tsx:81:4" data-component-path="src/components/ui/Button.tsx" data-component-line="81" data-component-end-line="115" data-component-file="Button.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22%5Bvar%3Atype%5D%22%2C%22%5Bspread%5D%22%3A%22true%22%7D"
      type={type}
      onClick={disabled || loading ? undefined : onClick}
      disabled={disabled || loading}
      className={`
        ${isRoundedFull ? 'rounded-full' : responsiveRadius}
        transition-all 
        duration-200 
        ease-in-out
        focus:outline-none 
        ${variants[variant]} 
        ${sizes[size]} 
        ${fullWidth ? 'w-full' : ''}
        ${disabled || loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} 
        ${loading ? 'relative' : ''}
        font-medium
        inline-flex
        items-center
        justify-center
        touch-manipulation
        ${className}
      `
        .trim()
        .replace(/\s+/g, ' ')}
      aria-disabled={disabled || loading}
      aria-busy={loading}
      aria-label={ariaLabel}
      aria-describedby={ariaDescribedBy}
      aria-pressed={ariaPressed}
      aria-expanded={ariaExpanded}
      {...props}
    >
      {loading && <LoadingSpinner data-component-id="src/components/ui/Button.tsx:113:18" data-component-path="src/components/ui/Button.tsx" data-component-line="113" data-component-end-line="113" data-component-file="Button.tsx" data-component-name="LoadingSpinner" data-component-content="%7B%22elementName%22%3A%22LoadingSpinner%22%7D" />}
      <span data-component-id="src/components/ui/Button.tsx:114:6" data-component-path="src/components/ui/Button.tsx" data-component-line="114" data-component-end-line="114" data-component-file="Button.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D" className={loading ? 'opacity-75' : ''}>{children}</span>
    </button>
  );
};

export default Button;
